import { FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import VideoPosterImage from '../assets/videoposter.svg';
import WorkFlowImage from '../assets/workflow-200h.png';
import ClientsImage from '../assets/clients-1400w.jpg';
import LocationImage from '../assets/location-200h.png';
import OfficeImage from '../assets/office-1400w.jpg';
import { CONTACT_EMAIL, FEATURES_HASH, PROFILE_HASH } from '../lib/constants';

const ItemTitle: FC<{
  id?: string;
  icon: ReactNode;
  title: string;
  subtitle?: string;
  description?: string;
}> = ({ id, icon, title, subtitle, description }) => {
  return (
    <>
      <div
        id={id}
        className="flex items-center flex-col
                   w-4/5 sm:w-full"
      >
        <div className="flex items-center justify-center w-12 h-12 rounded-lg flex-col bg-pink-gradient">
          {icon}
        </div>
        <div className="mt-6 bg-pink-gradient bg-clip-text text-transparent text-[32px] font-bold text-center">
          {title}
        </div>
        {subtitle && (
          <div className="mb-2 text-[32px] font-bold text-title-text">
            {subtitle}
          </div>
        )}
        {description && (
          <div className="text-description-text text-center text-[17px]">
            {description}
          </div>
        )}
      </div>
    </>
  );
};

const Divider = () => {
  return (
    <div
      className="w-[90%] h-px bg-gray-400 opacity-25
                 my-8 sm:my-16"
    />
  );
};

const ListItem: FC<{
  title: string;
  description: string;
}> = ({ title, description }) => {
  return (
    <div className="flex-none flex p-4 max-w-[500px] items-start flex-col">
      <div className="text-main-text mb-2 text-[21px] font-semibold">
        {title}
      </div>
      <div className="text-description-text text-[17px]">{description}</div>
    </div>
  );
};

const ListItemContainer: FC<{
  children: ReactNode;
}> = ({ children }) => {
  return (
    <div
      className="flex flex-col items-start
                 mr-0 lg:mr-16"
    >
      {children}
    </div>
  );
};

const DescriptionContainer: FC<{
  children: ReactNode;
}> = ({ children }) => {
  return (
    <div
      className="flex items-center justify-center w-full
                 mt-0 sm:mt-12
                 flex-col sm:flex-row"
    >
      {children}
    </div>
  );
};

const ExcelFeature = () => {
  const { t } = useTranslation();

  return (
    <>
      <ItemTitle
        id={FEATURES_HASH.slice(FEATURES_HASH.indexOf('#') + 1)}
        icon={
          <svg
            viewBox="0 0 1024 1024"
            className="w-6 h-6 text-black"
            fill="currentColor"
          >
            <path d="M363.722 722.052l41.298-57.816-45.254-45.256-57.818 41.296c-10.722-5.994-22.204-10.774-34.266-14.192l-11.682-70.084h-64l-11.68 70.086c-12.062 3.418-23.544 8.198-34.266 14.192l-57.818-41.298-45.256 45.256 41.298 57.816c-5.994 10.72-10.774 22.206-14.192 34.266l-70.086 11.682v64l70.086 11.682c3.418 12.060 8.198 23.544 14.192 34.266l-41.298 57.816 45.254 45.256 57.818-41.296c10.722 5.994 22.204 10.774 34.266 14.192l11.682 70.084h64l11.68-70.086c12.062-3.418 23.544-8.198 34.266-14.192l57.818 41.296 45.254-45.256-41.298-57.816c5.994-10.72 10.774-22.206 14.192-34.266l70.088-11.68v-64l-70.086-11.682c-3.418-12.060-8.198-23.544-14.192-34.266zM224 864c-35.348 0-64-28.654-64-64s28.652-64 64-64 64 28.654 64 64-28.652 64-64 64zM1024 384v-64l-67.382-12.25c-1.242-8.046-2.832-15.978-4.724-23.79l57.558-37.1-24.492-59.128-66.944 14.468c-4.214-6.91-8.726-13.62-13.492-20.13l39.006-56.342-45.256-45.254-56.342 39.006c-6.512-4.766-13.22-9.276-20.13-13.494l14.468-66.944-59.128-24.494-37.1 57.558c-7.812-1.892-15.744-3.482-23.79-4.724l-12.252-67.382h-64l-12.252 67.382c-8.046 1.242-15.976 2.832-23.79 4.724l-37.098-57.558-59.128 24.492 14.468 66.944c-6.91 4.216-13.62 8.728-20.13 13.494l-56.342-39.006-45.254 45.254 39.006 56.342c-4.766 6.51-9.278 13.22-13.494 20.13l-66.944-14.468-24.492 59.128 57.558 37.1c-1.892 7.812-3.482 15.742-4.724 23.79l-67.384 12.252v64l67.382 12.25c1.242 8.046 2.832 15.978 4.724 23.79l-57.558 37.1 24.492 59.128 66.944-14.468c4.216 6.91 8.728 13.618 13.494 20.13l-39.006 56.342 45.254 45.256 56.342-39.006c6.51 4.766 13.22 9.276 20.13 13.492l-14.468 66.944 59.128 24.492 37.102-57.558c7.81 1.892 15.742 3.482 23.788 4.724l12.252 67.384h64l12.252-67.382c8.044-1.242 15.976-2.832 23.79-4.724l37.1 57.558 59.128-24.492-14.468-66.944c6.91-4.216 13.62-8.726 20.13-13.492l56.342 39.006 45.256-45.256-39.006-56.342c4.766-6.512 9.276-13.22 13.492-20.13l66.944 14.468 24.492-59.13-57.558-37.1c1.892-7.812 3.482-15.742 4.724-23.79l67.382-12.25zM672 491.2c-76.878 0-139.2-62.322-139.2-139.2s62.32-139.2 139.2-139.2 139.2 62.322 139.2 139.2c0 76.878-62.32 139.2-139.2 139.2z"></path>
          </svg>
        }
        title={t("Mr. Automatic's main functions")}
        subtitle={t('Data Scraping')}
        description={t(
          'Scrape website data into Online Excel, CSV, and other files.',
        )}
      />
      <div
        className="flex items-center justify-center w-full
                   flex-col lg:flex-row
                   mt-6 sm:mt-12"
      >
        <video
          className="w-full h-full"
          src="/excel_low_quality.webm"
          loop={true}
          poster={VideoPosterImage}
          preload="auto"
          autoPlay={true}
          muted={true}
          playsInline={true}
        />
        <ListItemContainer>
          <ListItem
            title={t('Online Excel Reading and Writing')}
            description={t(
              "Mr. Automatic's one of the best features. It allows you to directly write the collected data into Online Excel.",
            )}
          />
          <ListItem
            title={t('Schedule Setting')}
            description={t(
              'You can flexibly configure data scraping on a monthly, weekly, or hourly basis.',
            )}
          />
          <ListItem
            title={t('Various Input and Output Formats')}
            description={t(
              'Supports a wide range of data input and output formats, including PDF, JSON, Excel, and CSV.',
            )}
          />
        </ListItemContainer>
      </div>
    </>
  );
};

const ServiceFlow = () => {
  const { t } = useTranslation();

  return (
    <>
      <ItemTitle
        icon={
          <img
            className="w-6 object-cover"
            src={WorkFlowImage}
            alt="service-flow"
          />
        }
        title={t('Service Process')}
        description={t('We meet the individual needs of each customer.')}
      />
      <DescriptionContainer>
        <ListItemContainer>
          <ListItem
            title={t('Select Data to Collect')}
            description={t(
              'Discuss what data to collect, its sources, and the output formats.',
            )}
          />
          <ListItem
            title={t('Start Data Collection')}
            description={t(
              'Data will be scraped from relevant sites and saved into the specified format.',
            )}
          />
          <ListItem
            title={t('Deliver Collected Data')}
            description={t(
              "Collected data will be delivered in various formats according to the customer's requests.",
            )}
          />
        </ListItemContainer>
        <img
          className="w-[450px] object-cover border border-description-text rounded-lg
                     mb-5 sm:mb-0"
          src={ClientsImage}
          alt="clients"
        />
      </DescriptionContainer>
    </>
  );
};

const AboutCompany = () => {
  const { t } = useTranslation();

  return (
    <>
      <ItemTitle
        id={PROFILE_HASH.slice(PROFILE_HASH.indexOf('#') + 1)}
        icon={
          <img
            className="w-6 object-cover"
            src={LocationImage}
            alt="location"
          />
        }
        title={t('Company Profile')}
      />
      <DescriptionContainer>
        <ListItemContainer>
          <ListItem
            title={t('Contact Information')}
            description={CONTACT_EMAIL}
          />
          <ListItem title={t('Address')} description={t('COMPANY_ADDRESS')} />
          <ListItem
            title={t('Establishment')}
            description={t('ESTABLISHMENT_DAY')}
          />
          <ListItem
            title={t('Representative Director')}
            description={t('REPRESENTATIVE_DIRECTOR')}
          />
          <ListItem
            title={t('Business Activities')}
            description={t('BUSINESS_ACTIVITIES')}
          />
        </ListItemContainer>
        <img
          className="w-[350px] object-cover border border-description-text rounded-lg"
          src={OfficeImage}
          alt="office"
        />
      </DescriptionContainer>
    </>
  );
};

const MainContentsSection = () => {
  return (
    <section
      className="flex-none w-full max-w-[1320px] flex items-center flex-col py-24
                 px-8 sm:px-12"
    >
      <ExcelFeature />
      <Divider />
      <ServiceFlow />
      <Divider />
      <AboutCompany />
    </section>
  );
};

export default MainContentsSection;
